import { type FC, useEffect, useState } from "react";
import { Checkbox, Icon } from "semantic-ui-react";
import { type PaymentFormData, type PaymentPack, PaymentStatus, type PurchasePrice } from "./types";
import Section from "components/wizard/Section";
import WizardModal from "components/wizard/WizardModal/WizardModal";
import styles from "./PaymentModal.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { useRtn } from "hooks/useRtn";
import {
  PurchasePackAllSuccess,
  PurchasePacksFailure,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import environmentConfig from "configuration/environmentConfig";
import { type WizardStepHandlers } from "interfaces/IWizardStepsManager";
import { usePaymentForm } from "./hooks/usePaymentForm";
import type { AppDispatch, RootState } from "../../../Application/globaltypes/redux";
import {
  selectStateMapping,
  selectStates,
  selectStatesError,
  selectPaymentError,
  selectPaymentStatus,
  selectPurchasePrice,
  selectPurchasePriceError,
} from "./state/selectors";
import { resetStatesError } from "./state/slices/statesSlice";
import { errPayment, resetPaymentError, setPaymentSuccess } from "./state/slices/paymentProcessingSlice";
import { resetPurchasePrice, resetPurchasePriceError } from "./state/slices/purchasePriceSlice";
import { fetchStates } from "./state/thunks/statesThunk";
import { bindAction } from "../../../../interfaces";
import { fetchPurchasePrice } from "./state/thunks/purchasePriceThunk";
import {
  HIDE_GENERAL_LOADER,
  SHOW_GENERAL_LOADER,
} from "../../../Application/actions/generalLoader/generalLoaderActionTypes";
import { processPayment as processPaymentAction } from "./state/thunks/paymentProcessingThunk";
import { remapPaymentFormToRequest } from "./utils/remapPaymentData";
import { reset } from "../../../Accounts/EditAccount/state/slices/editAccountPackLicenseSlice";
import { connect, type ConnectedProps } from "react-redux";
import ConfirmationStep from "./steps/ConfirmationStep";
import PaymentDetailsStep from "./steps/PaymentDetailsStep";
import PaymentErrorBanner from "./components/PaymentErrorBanner/PaymentErrorBanner";
import { Typography } from "../../../../components/common/Typography/Typography";

export interface PaymentModalProps {
  paymentPacks: PaymentPack[];
  onPaymentSuccess: (id: number) => void;
  onClose: () => void;
}

enum PaymentSteps {
  PaymentDetails,
  Confirmation,
}

const SUCCESS_PAYMENT_EVENTS = [PurchasePackAllSuccess];
const FAILED_PAYMENT_EVENTS = [PurchasePacksFailure];

export type PaymentModalPropsAll = PaymentModalProps & PropsFromRedux;

export const PaymentModal: FC<PaymentModalPropsAll> = ({
  paymentPacks,
  errorMessage,
  onClose,
  onGoBack,
  onLoad,
  onPaymentError,
  onPaymentSuccess,
  paymentStatus,
  processPayment,
  stateMapping,
  states,
  fetchPurchasePriceAction,
  purchasePrice,
  hasStatesLoaded,
  onCloseAction,
  onPaymentSuccessAction,
}) => {
  const [termAccepted, setTermAccepted] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<PaymentSteps>(PaymentSteps.PaymentDetails);

  const { submitForm, isValid, formik } = usePaymentForm({
    purchasePrice,
    processPayment,
    paymentStatus,
    packs: paymentPacks,
  });

  const { billingInfo, packs } = formik.values;

  useRtn(SUCCESS_PAYMENT_EVENTS, () => {
    onPaymentSuccessHandler(packs[0].packId);
  });
  useRtn(FAILED_PAYMENT_EVENTS, ({ error }: { error: string }) => {
    onPaymentError(error || "An error occurred while processing your payment. Please try again.");
  });

  useEffect(() => {
    onLoad(hasStatesLoaded);
  }, [onLoad, hasStatesLoaded]);

  useEffect(() => {
    if (isValid) fetchPurchasePriceAction(packs, billingInfo);
  }, [isValid, packs, billingInfo, fetchPurchasePriceAction]);

  const goBack = () => {
    setActiveStep(PaymentSteps.PaymentDetails);
    onGoBack();
  };

  const stepActions: Record<number, WizardStepHandlers> = {
    [PaymentSteps.PaymentDetails]: {
      onNext: () => setActiveStep(PaymentSteps.Confirmation),
    },
    [PaymentSteps.Confirmation]: {
      onPrevious: goBack,
    },
  };

  const onCloseHandler = () => {
    onCloseAction();
    onClose();
  };

  const onPaymentSuccessHandler = (packId: number) => {
    onPaymentSuccessAction();
    onPaymentSuccess(packId);
  };

  return (
    <WizardModal
      title="Purchase Pack"
      isOpen
      onClose={onCloseHandler}
      onFinish={submitForm}
      stepActions={stepActions}
      activeStepIndex={activeStep}
      wizardDescription={
        <div className={styles.description}>
          <FontAwesomeIcon icon={faLock} /> Secure Payment Form
        </div>
      }
    >
      <Section
        label={"Payment Details"}
        isLocked={
          !isValid ||
          paymentStatus === PaymentStatus.Loading ||
          paymentStatus === PaymentStatus.Error ||
          !!purchasePrice.error ||
          purchasePrice.isLoading
        }
        required
      >
        {!!errorMessage && <PaymentErrorBanner message={errorMessage} />}
        <PaymentDetailsStep formik={formik} purchasePrice={purchasePrice} states={states} />
      </Section>

      <Section
        label={"Confirmation"}
        isLocked={paymentStatus === PaymentStatus.Loading || paymentStatus === PaymentStatus.Error || !termAccepted}
        finishButton={
          <span>
            <Icon name={"lock"} className={`inverted ${styles.confirmationIcon}`} /> Pay Now
          </span>
        }
        extraAction={
          <div className={styles.terms}>
            <Checkbox checked={termAccepted} onChange={(_, data) => setTermAccepted(!!data.checked)} />
            <Typography>
              I've read and agree to the{" "}
              <a href={environmentConfig.termsAndConditionsMarketplaceUrl} target="_blank" rel="noreferrer">
                Terms and Conditions
              </a>
              .
            </Typography>
          </div>
        }
      >
        {paymentStatus === PaymentStatus.Error && errorMessage && <PaymentErrorBanner message={errorMessage} />}
        <ConfirmationStep
          goBack={goBack}
          paymentDetails={formik.values}
          stateMapping={stateMapping}
          purchasePrice={purchasePrice}
        />
      </Section>
    </WizardModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  states: selectStates(state),
  stateMapping: selectStateMapping(state),
  errorMessage: selectPaymentError(state) || selectPurchasePriceError(state) || selectStatesError(state),
  paymentStatus: selectPaymentStatus(state),
  purchasePrice: selectPurchasePrice(state),
  hasStatesLoaded: selectStates(state).length > 0,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  const resetErrors = () => {
    dispatch(resetStatesError());
    dispatch(resetPurchasePriceError());
    dispatch(resetPaymentError());
  };

  return {
    onLoad: (hasStatesLoaded: boolean) => {
      resetErrors();
      dispatch(resetPurchasePrice());
      if (!hasStatesLoaded) dispatch(fetchStates());
    },
    fetchPurchasePriceAction: bindAction(fetchPurchasePrice, dispatch),
    processPayment: async (data: { formData: PaymentFormData; purchasePrice: PurchasePrice }) => {
      try {
        dispatch({ type: SHOW_GENERAL_LOADER });
        await dispatch(processPaymentAction(remapPaymentFormToRequest(data)));
      } catch (error) {
        dispatch({ type: HIDE_GENERAL_LOADER });
      }
    },
    onPaymentSuccessAction: () => {
      dispatch({ type: HIDE_GENERAL_LOADER });
      dispatch(setPaymentSuccess());
      resetErrors();
      dispatch(resetPurchasePrice());
      dispatch(resetPurchasePriceError());
      dispatch(reset());
    },
    onPaymentError: (error: string) => {
      dispatch({ type: HIDE_GENERAL_LOADER });
      dispatch(errPayment(error));
    },
    onGoBack: resetErrors,
    onCloseAction: () => {
      resetErrors();
      dispatch(resetPurchasePrice());
      dispatch(reset());
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const ConnectedComponent = connector(PaymentModal);

export type PropsFromRedux = ConnectedProps<typeof connector>;

export default ConnectedComponent;
